/**
 * Actions whose response is consumed by the submitting lesson panel and whose
 * writes do not change any field returned by the lesson loader.
 *
 * Code save/submit/reset are deliberately absent: `submittedLessonIds` still
 * projects qualifying `user_code_progress`, so those writes can change loader
 * output even before the separate canonical completion action runs.
 */
export const NON_REVALIDATING_LESSON_FETCHER_INTENTS = [
  "code-execution",
  "bash-execution",
  "guest-submit-code",
  "get-code-progress",
  "save-web-files",
  "get-web-files",
  "get-quiz-progress",
  "start-quiz-session",
  "submit-quiz-answer",
  "get-bookmark-status",
  "toggle-bookmark",
  "get-note",
  "save-note",
  // 解答を返すだけの読み取り。ログインを見て no-store で返すので、
  // loader が出す値は 1 つも変わらない
  "coding-problem-solution",
  // ゆめさくコーチ。mastery は表示だけの値で DB に残らず、loader の
  // 射影を1つも変えない。ai-tutor-sql-result は route 側で || の3項目に
  // あるため走査テストの対象外だが、同じく再取得の要らない読み取り
  "ai-tutor-start",
  "ai-tutor-answer",
  "ai-tutor-sql-result",
  // AI に書かせる 2 本。応答はパネルの中だけで使い、loader の射影を
  // 1 つも変えない。2026-09-20 まで素の fetch で叩いていて、
  // single-fetch が HTML を返すせいで本番では一度も応答を読めていなかった
  "run-prompt",
  "run-agent",
] as const;

/** These actions mutate data projected by the lesson loader, so they revalidate. */
export const REVALIDATING_LESSON_ACTION_INTENTS = [
  "ai-chat",
  "create-chat-session",
  "add-chat-message",
  "update-lesson-content",
  "update-sql-lesson",
  "create-coding-problem",
  "update-coding-problem",
  "save-code",
  "submit-code",
  "reset-code-progress",
  "mark-lesson-complete",
  "finish-quiz-session",
  "updateQuizQuestions",
] as const;

/**
 * These panels use window.fetch directly and never enter Router revalidation.
 *
 * **空のまま残してある。** run-prompt と run-agent がここにいたが、
 * single-fetch では `.data` の付かない POST が HTML を返すので、応答を
 * 一度も読めていなかった。素の fetch でアクションを叩く口はもう無い。
 * 新しく足したくなったら、まず応答が読めるかを本番で確かめること。
 */
export const RAW_FETCH_LESSON_ACTION_INTENTS = [] as const;

type LocalStateIntent =
  (typeof NON_REVALIDATING_LESSON_FETCHER_INTENTS)[number];

export interface LessonFetcherRevalidationArgs {
  currentUrl: URL;
  nextUrl: URL;
  formMethod?: string;
  formAction?: string;
  formData?: FormData;
  actionStatus?: number;
  actionResult?: unknown;
}

const localStateIntents = new Set<string>(
  NON_REVALIDATING_LESSON_FETCHER_INTENTS,
);

function normalizedPathname(pathname: string): string {
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "");
}

function actionUrl(args: LessonFetcherRevalidationArgs): URL | null {
  if (!args.formAction) return null;
  try {
    return new URL(args.formAction, args.currentUrl);
  } catch {
    return null;
  }
}

function actionResultExpiredSession(result: unknown): boolean {
  return (
    typeof result === "object" &&
    result !== null &&
    "sessionExpired" in result &&
    (result as { sessionExpired?: unknown }).sessionExpired === true
  );
}

function localStateIntent(
  formData: FormData | undefined,
): LocalStateIntent | null {
  const intent = formData?.get("intent");
  return typeof intent === "string" && localStateIntents.has(intent)
    ? (intent as LocalStateIntent)
    : null;
}

function isSuccessfulSameLocationPost(
  args: LessonFetcherRevalidationArgs,
): boolean {
  if (args.formMethod?.toUpperCase() !== "POST") return false;
  if (!localStateIntent(args.formData)) return false;
  if (
    args.actionStatus !== undefined &&
    (args.actionStatus < 200 || args.actionStatus >= 300)
  ) {
    return false;
  }
  if (actionResultExpiredSession(args.actionResult)) return false;
  return (
    normalizedPathname(args.currentUrl.pathname) ===
      normalizedPathname(args.nextUrl.pathname) &&
    args.currentUrl.search === args.nextUrl.search
  );
}

export function isPublicLessonActionPath(pathname: string): boolean {
  const segments = normalizedPathname(pathname).split("/").filter(Boolean);
  return (
    segments.length === 4 &&
    segments[0] === "learn" &&
    segments[1] === "courses"
  );
}

/** Used by the lesson route itself. Never suppress a cross-route submission. */
export function shouldSkipLessonRouteRevalidation(
  args: LessonFetcherRevalidationArgs,
): boolean {
  if (!isSuccessfulSameLocationPost(args)) return false;
  const submittedTo = actionUrl(args);
  if (!submittedTo) return false;
  return (
    normalizedPathname(submittedTo.pathname) ===
    normalizedPathname(args.currentUrl.pathname)
  );
}

/** Used by root, scoped to public lesson actions so other routes cannot collide. */
export function shouldSkipRootRevalidationForLessonFetcher(
  args: LessonFetcherRevalidationArgs,
): boolean {
  if (!isSuccessfulSameLocationPost(args)) return false;
  const submittedTo = actionUrl(args);
  return submittedTo !== null && isPublicLessonActionPath(submittedTo.pathname);
}
